import { CheckCircleFilled, PlusOutlined } from "@ant-design/icons";
import { Button, Empty, Progress, Spin } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetSavingsGoals } from "../../api/api.get";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { useCurrencyContext } from "../../providers/currency.provider";
import { useGroupContext } from "../../providers/groups.provider";
import { capitalize } from "../../utils/functions";
import { toFixedDisplay } from "../../utils/number.utils";
import CreateSavingsGoalDialog from "./create.savings.goal.dialog";

export default function SavingsGoalsSection() {
  const navigate = useNavigate();
  const { defaultGroupId } = useGroupContext();
  const { currency } = useCurrencyContext();

  const {
    data: goals,
    isPending,
    refetch,
  } = useGetSavingsGoals(defaultGroupId);

  const [showAdd, setShowAdd] = useState(false);

  return (
    <Card className="bg-white mb-8">
      {showAdd && (
        <CreateSavingsGoalDialog
          open={showAdd}
          onCancel={() => setShowAdd(false)}
          onSuccess={() => {
            setShowAdd(false);
            refetch();
          }}
        />
      )}

      <CardHeader>
        <CardTitle className="flex items-center gap-2">Savings Goals</CardTitle>
        <CardDescription>
          <div className="flex justify-between items-center">
            <span>Set targets and track funds you've secured toward them</span>
            <Button
              type="link"
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setShowAdd(true)}
            >
              Add Goal
            </Button>
          </div>
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isPending && <Spin size="large" />}

        {!isPending && (!goals || goals.length === 0) && (
          <Empty description="No savings goals yet. Create your first!" />
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {goals?.map((goal) => {
            const percent =
              goal.targetAmount > 0
                ? (goal.savedAmount / goal.targetAmount) * 100
                : 0;
            return (
              <button
                key={goal.id}
                onClick={() => navigate(`/savings-goals/${goal.id}`)}
                className="text-left border border-slate-200 rounded-lg p-4 hover:shadow-md hover:border-primary/50 transition bg-slate-50"
              >
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-semibold text-foreground">
                    {capitalize(goal.name)}
                  </h4>
                  {goal.completed && (
                    <span className="inline-flex items-center gap-1 text-green-600 text-xs">
                      <CheckCircleFilled /> Done
                    </span>
                  )}
                </div>

                <Progress
                  percent={Math.min(Math.round(percent), 100)}
                  status={goal.completed ? "success" : "active"}
                  size="small"
                />

                <div className="flex justify-between text-xs mt-2">
                  <span className="text-muted-foreground">
                    {currency}
                    {toFixedDisplay(goal.savedAmount)} of {currency}
                    {toFixedDisplay(goal.targetAmount)}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  by {dayjs(goal.targetDate).format("DD MMM YYYY")}
                </p>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
